import { useState } from "react";
import { FiSearch, FiX, FiUsers, FiUser } from "react-icons/fi";
import toast from "react-hot-toast";
import api from "../../utils/api";
import useChatStore from "../../store/chatStore";
import { useSocket } from "../../context/SocketContext";
import { getInitials } from "../../utils/helpers";
import styles from "./Modal.module.css";

const NewChatModal = ({ onClose }) => {
  const { addConversation, setActiveConversation } = useChatStore();
  const socketRef = useSocket();

  const [tab,       setTab]       = useState("direct");
  const [groupName, setGroupName] = useState("");
  const [selected,  setSelected]  = useState([]);  // array of user objects
  const [query,     setQuery]     = useState("");
  const [results,   setResults]   = useState([]);
  const [searching, setSearching] = useState(false);

  // ── Search users ───────────────────────────────────────────────
  const handleSearch = async (e) => {
    const val = e.target.value;
    setQuery(val);
    if (!val.trim()) return setResults([]);
    setSearching(true);
    try {
      const res = await api.get(`/users/search?q=${encodeURIComponent(val.trim())}`);
      setResults(res.data.users || []);
    } catch {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  // ── Direct chat ────────────────────────────────────────────────
  const handleDirectChat = async (userId) => {
    try {
      const res = await api.post("/conversations", { userId });
      const conv = res.data.conversation;
      addConversation(conv);
      setActiveConversation(conv);
      socketRef?.current?.emit("conversation:new", { userId, conversation: conv });
      onClose();
    } catch {
      toast.error("Failed to start conversation");
    }
  };

  // ── Toggle user in group selection ─────────────────────────────
  const toggleSelect = (user) => {
    setSelected((prev) =>
      prev.some((u) => u._id === user._id)
        ? prev.filter((u) => u._id !== user._id)
        : [...prev, user]
    );
  };

  const isSelected = (user) => selected.some((u) => u._id === user._id);

  // ── Create group ───────────────────────────────────────────────
  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      toast.error("Enter a group name");
      return;
    }
    if (selected.length === 0) {
      toast.error("Select at least 1 member");
      return;
    }
    try {
      const res = await api.post("/conversations/group", {
        name: groupName.trim(),
        participants: selected.map((u) => u._id),
      });
      const conv = res.data.conversation;
      addConversation(conv);
      setActiveConversation(conv);
      selected.forEach((u) => {
        socketRef?.current?.emit("conversation:new", { userId: u._id, conversation: conv });
      });
      toast.success(`Group "${groupName.trim()}" created!`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create group");
    }
  };

  const canCreate = groupName.trim().length > 0 && selected.length > 0;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className={styles.modalHeader}>
          <h3>{tab === "direct" ? "New Chat" : "New Group"}</h3>
          <button className={styles.closeBtn} onClick={onClose}>
            <FiX size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${tab === "direct" ? styles.activeTab : ""}`}
            onClick={() => { setTab("direct"); setSelected([]); }}
          >
            <FiUser size={13} /> Direct
          </button>
          <button
            className={`${styles.tab} ${tab === "group" ? styles.activeTab : ""}`}
            onClick={() => setTab("group")}
          >
            <FiUsers size={13} /> Group
          </button>
        </div>

        {/* Group name input */}
        {tab === "group" && (
          <input
            type="text"
            className="input-field"
            placeholder="Group name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            style={{ margin: "0 0 12px" }}
            autoFocus
          />
        )}

        {/* Search */}
        <div className={styles.searchBar}>
          <FiSearch size={14} />
          <input
            type="text"
            placeholder="Search users..."
            value={query}
            onChange={handleSearch}
            className={styles.searchInput}
            autoFocus={tab === "direct"}
          />
          {query && (
            <button onClick={() => { setQuery(""); setResults([]); }}>
              <FiX size={13} />
            </button>
          )}
        </div>

        {/* Selected chips (group mode) */}
        {tab === "group" && selected.length > 0 && (
          <div className={styles.selectedChips}>
            {selected.map((u) => (
              <span key={u._id} className={styles.chip}>
                {u.name}
                <button onClick={() => toggleSelect(u)}><FiX size={11} /></button>
              </span>
            ))}
          </div>
        )}

        {/* Results */}
        <div className={styles.resultsList}>
          {searching && <div className={styles.hint}>Searching...</div>}
          {!searching && query && results.length === 0 && (
            <div className={styles.hint}>No users found</div>
          )}
          {!searching && !query && (
            <div className={styles.hint}>Type a name or email to search</div>
          )}
          {results.map((u) => (
            <div
              key={u._id}
              className={`${styles.userRow} ${tab === "group" && isSelected(u) ? styles.selectedRow : ""}`}
              onClick={() => tab === "direct" ? handleDirectChat(u._id) : toggleSelect(u)}
            >
              <div className="avatar" style={{ width: 38, height: 38, fontSize: 13, flexShrink: 0 }}>
                {u.avatar
                  ? <img src={u.avatar} alt={u.name} style={{ width: "100%", height: "100%", borderRadius: "50%" }} />
                  : getInitials(u.name)}
              </div>
              <div className={styles.userInfo}>
                <span className={styles.userName}>{u.name}</span>
                <span className={styles.userEmail}>{u.email}</span>
              </div>
              {u.isOnline && <span className={styles.onlineBadge}>Online</span>}
              {tab === "group" && isSelected(u) && (
                <span style={{ color: "var(--primary)", fontSize: 18, fontWeight: 700 }}>✓</span>
              )}
            </div>
          ))}
        </div>

        {/* Create Group button */}
        {tab === "group" && (
          <button
            className={styles.createBtn}
            onClick={handleCreateGroup}
            disabled={!canCreate}
            style={{
              margin: "10px 14px 14px",
              width: "calc(100% - 28px)",
              padding: "12px",
              background: canCreate ? "var(--primary)" : "var(--border)",
              color: canCreate ? "white" : "var(--text-muted)",
              border: "none",
              borderRadius: "10px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: canCreate ? "pointer" : "not-allowed",
              fontFamily: "inherit",
              transition: "all 0.15s",
            }}
          >
            {selected.length === 0
              ? "Select members to create group"
              : `Create Group (${selected.length} member${selected.length > 1 ? "s" : ""})`}
          </button>
        )}
      </div>
    </div>
  );
};

export default NewChatModal;
